import { generateMessageId } from '@lingui/message-utils/generateMessageId';
import pseudoLocalise from './pseudo-localise';

const DEBUG = false;

function debug(...args) {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

function error(...args) {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
}

export function loadLinguiCatalogs(baseUrl, locales) {
  // Before we can continue, we need to get the message JSON for each locale
  // from the CDN.
  //
  // Return a promise that will resolve once we have all of them and have
  // compiled them into the data that Lingui requires.

  const catalogPromises = locales.map(locale => loadCatalog(baseUrl, locale));

  return new Promise((resolve) => {
    Promise.all(catalogPromises)
      .then((catalogs) => {
        const localeCatalogs = locales.reduce((lc, locale, i) => {
          lc[locale] = catalogs[i];

          return lc;
        }, { });

        resolve(localeCatalogs);
      });
  });
}

function loadCatalog(baseUrl, locale) {
  // Return a promise that will resolve once we've retrieved the message JSON for the
  // specified locale.
  //
  // What we return is the compiled version of that JSON.
  //
  // If, for some reason, an error occurs retrieving the JSON for the specified locale,
  // we attempt to retrieve the en-AU file, instead, which should always exist.  If so,
  // we use it.  If not, well there's not much we can do, other than log a message that
  // something went wrong.

  return new Promise((resolve) => {
    window.fetch(`${baseUrl}/${locale}.json`)
      .then(response => response.json())
      .then((translations) => {
        debug('loadCatalog() - translations =', JSON.stringify(translations, null, 2));
        const compiled = compile(locale, translations);

        resolve(compiled);
      })
      .catch((e) => {
        error(`loadCatalog() - received an error loading ${locale}; replacing with en-AU. Error was...`,
          e);

        window.fetch(`${baseUrl}/en-AU.json`)
          .then(response => response.json())
          .then((translations) => {
            debug('loadCatalog() - translations =', JSON.stringify(translations, null, 2));
            const compiled = compile(locale, translations);

            resolve(compiled);
          }).catch((error2) => {
            e(`loadCatalog() - the fallback to en-AU failed with error... ${error2}`);
            resolve({});
          });
      });
  });
}

export function compile(locale, json) {
  debug('compile() - locale =', locale, 'json =', JSON.stringify(json, null, 2));

  if (locale === 'en-PL') {
    json = pseudoLocalise(json);
  }

  const extractedMessages = {};
  if (json) {
    Object.keys(json).forEach((key) => {
      extractedMessages[key] = json[key];
      extractedMessages[generateMessageId(key)] = json[key];
    });
  }

  return { languageData: {}, messages: extractedMessages };
}
